import { useState, useRef, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import * as translator from '../../../../utils/translator';
import * as http from '../../../../utils/http';
import { toLocaleDateString } from '../../../../utils/helpers';
import './MyAccountTestKitResults.css';

interface MyAccountTestKitResultsProps {
	language: string;
	testName: string;
	orderHashID: string;
	resultsDate?: string | null;
}

const DOWNLOAD_DEBOUNCE_MS = 60_000;

const MyAccountTestKitResults: React.FC<MyAccountTestKitResultsProps> = ({
	language,
	testName,
	orderHashID,
	resultsDate,
}) => {
	const { country } = useOutletContext<any>();
	const [isDownloading, setIsDownloading] = useState(false);
	const lastDownloadTimestamp = useRef<number>(0);

	const handleDownloadPDF = useCallback(() => {
		const now = Date.now();
		if (now - lastDownloadTimestamp.current < DOWNLOAD_DEBOUNCE_MS) {
			return;
		}

		lastDownloadTimestamp.current = now;
		setIsDownloading(true);

		http
			.getFromServer(`${country}/account/orders/${orderHashID}/test-results/download`, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.GENERATE_FILE])
			.then((response: any) => {
				const blob = new Blob([response.data], { type: 'application/pdf' });
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = `test-results-${orderHashID}.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
			})
			.catch((error) => {
				window.clientLogger.error('Error downloading test results PDF', error);
				lastDownloadTimestamp.current = 0;
			})
			.finally(() => {
				setIsDownloading(false);
			});
	}, [country, orderHashID]);

	const resultsReadyText = translator
		.translate(language, 'my_account', 'MyOrders/details/testKitResults/results_ready')
		.replace(':testName', testName);

	const formattedDate = resultsDate
		? toLocaleDateString(resultsDate, language, { day: 'numeric', month: 'short', year: 'numeric' })
		: '';

	return (
		<div className="test-kit-results-banner">
			<div className="test-kit-results-title">
				{translator.translate(language, 'my_account', 'MyOrders/details/testKitResults/banner_title')}
			</div>
			<div className="test-kit-results-info">
				<p className="test-kit-results-text">{resultsReadyText}</p>
				{formattedDate && <p className="test-kit-results-date">{formattedDate}</p>}
			</div>
			<div className="test-kit-results-actions">
				<button className="test-kit-results-download-btn" onClick={handleDownloadPDF} disabled={isDownloading}>
					{isDownloading
						? translator.translate(language, 'my_account', 'MyOrders/details/testKitResults/downloading')
						: translator.translate(language, 'my_account', 'MyOrders/details/testKitResults/download_pdf')}
				</button>
			</div>
		</div>
	);
};

export default MyAccountTestKitResults;
